/* Iter 418.ae (Dirk, 2026-02-28) — Sticky-non-blocking announcement banner
   for the portal. Fetches unacknowledged announcements addressed to the
   current user and shows one banner per announcement. Priority tints the
   colour. Tap → modal with full body → Acknowledge writes the ack row and
   the banner clears for that user. Polls every 60s so a new announcement
   pops in-session without a full reload. */

import { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import api from "../../lib/api";

const COLORS = {
  info:     { bar: "#1FB6C1", ring: "#1FB6C133", label: "Info" },
  warn:     { bar: "#f59e0b", ring: "#f59e0b33", label: "Warning" },
  critical: { bar: "#ef4444", ring: "#ef444433", label: "Critical" },
};

export default function PortalAnnouncementBanner() {
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/portal/me/announcements");
      setRows(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const ack = async () => {
    if (!active || busy) return;
    setBusy(true);
    try {
      await api.post(`/portal-announcements/${active.id}/ack`);
      setRows((prev) => prev.filter((r) => r.id !== active.id));
      setActive(null);
    } catch { /* silent */ }
    setBusy(false);
  };

  if (rows.length === 0) return null;

  return (
    <>
      <div className="sticky top-0 z-40 flex flex-col gap-1 p-2" data-testid="portal-announcement-banner">
        {rows.map((r) => {
          const c = COLORS[r.priority] || COLORS.info;
          return (
            <button
              key={r.id}
              type="button"
              onClick={() => setActive(r)}
              className={`w-full text-left rounded-md border p-2.5 flex items-center gap-2 hover:brightness-110 animate-pulse`}
              style={{ borderColor: c.bar, background: c.ring }}
              data-testid={`portal-announcement-${r.id}`}
            >
              <Bell className="w-4 h-4 shrink-0" style={{ color: c.bar }} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-white truncate">{r.title}</div>
                <div className="text-[11px] text-white/80 truncate">
                  {c.label} · Tap to read {r.require_ack ? "+ acknowledge" : ""}
                </div>
              </div>
            </button>
          );
        })}
      </div>
      {active && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3" role="dialog">
          <div className="hv-card w-full max-w-md p-4 max-h-[90vh] overflow-y-auto" data-testid="portal-announcement-modal">
            <div className="flex items-start gap-2">
              <Bell className="w-5 h-5 mt-0.5 shrink-0" style={{ color: (COLORS[active.priority] || COLORS.info).bar }} />
              <div className="min-w-0 flex-1">
                <h3 className="font-display text-base font-bold">{active.title}</h3>
                <div className="text-[10px] uppercase tracking-wider text-[#647087] mt-0.5">
                  From HidroVerse · {new Date(active.created_at).toLocaleString()}
                </div>
              </div>
              {!active.require_ack && (
                <button type="button" onClick={() => setActive(null)} className="hv-btn hv-btn-ghost">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <p className="mt-3 text-sm text-slate-100 whitespace-pre-wrap">{active.body}</p>
            <div className="flex justify-end gap-2 mt-5 pt-3 border-t border-white/5">
              {!active.require_ack && (
                <button type="button" onClick={() => setActive(null)} className="hv-btn hv-btn-ghost">Close</button>
              )}
              <button
                type="button"
                onClick={ack}
                disabled={busy}
                className="hv-btn hv-btn-primary"
                data-testid="portal-announcement-ack"
              >
                {busy ? "Saving…" : (active.require_ack ? "I acknowledge" : "Got it")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
